import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { DataGrid } from '@mui/x-data-grid'
import { Grid, Paper } from '../../../../../components/mui/Core/index'
import { fetchOrderAllocations } from '../../order-allocations-slice'
import LinkButton from '../../../../../components/LinkButton/link-button'
import GenericSnackbar from '../../../../../components/GenericSnackbar/generic-snackbar'
import { setSnackbar } from '../../../../../components/GenericSnackbar/generic-snackbar-slice'
import { setSelectedOrder } from '../../../../outboundorders/Orders/order-slice'
import StatusChip from '../../../../../components/genericcomponents/StatusChip/status-chip'
import GenericTablePagination from '../../../../../components/GenericTablePagination/generic-table-pagination'
import Constants from '../../../../../constants/en.json'
import { getDateTimeFormattedValue } from '../../../../../utils/string-utils'

export function WmsOrderAllocationsTable() {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [rowsPerPage, setPageSize] = useState(25)
  const totalRowsCount = useSelector((state) => state.orderAllocations.totalRowsCount)
  const [page, setPage] = useState(0)
  const [sortDirection, setSortDirection] = useState('desc')
  const [sortProperty, setSortProperty] = useState('createdOn')

  const selectedInventoryItem = useSelector((state) => state.inventory.selectedInventoryItem)
  const orderAllocations = useSelector((state) => state.orderAllocations.orderAllocations)
  const isFetchingOrderAllocations = useSelector(
    (state) => state.orderAllocations.isFetchingOrderAllocations,
  )
  const fetchOrderAllocationsError = useSelector(
    (state) => state.orderAllocations.fetchingOrderAllocationsError,
  )

  useEffect(() => {
    if (selectedInventoryItem) {
      const value = { page, rowsPerPage, sortDirection, sortProperty }
      dispatch(fetchOrderAllocations(value))
    }
  }, [dispatch, page, rowsPerPage, selectedInventoryItem, sortDirection, sortProperty])

  useEffect(() => {
    /* When error occurs, display snackbar */
    if (fetchOrderAllocationsError) {
      dispatch(
        setSnackbar({
          message: fetchOrderAllocationsError?.message,
          severity: 'error',
          action: true,
        }),
      )
    }
  }, [fetchOrderAllocationsError, dispatch])

  const handleChangePage = (event, newPage) => {
    setPage(Number(newPage))
  }

  const handleChangeRowsPerPage = (event) => {
    setPageSize(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleOrderLinkClick = (order) => {
    const orderRoute = '/orderdetails'
    dispatch(setSelectedOrder(order))
    navigate(orderRoute)
  }

  const handleSortChange = (sort) => {
    if (sort.length === 0) {
      setSortDirection('desc')
      return
    }
    if (sort[0]?.field !== 'shipToName') {
      setSortProperty(sort[0].field)
    }
    if (sort[0]?.field === 'shipToName') {
      setSortProperty('ship_to_custname')
    }
    if (sort.length > 0) {
      setSortDirection(sort[0].sort)
    }
  }

  const rows = orderAllocations.map((row) => ({
    id: row.wms_order,
    wms_order: row.wms_order,
    po: row.ship_po,
    lines: row.num_line,
    createdOn: row.createdOn,
    orderStatus: row.orderStatus,
    shipToName: row.ship_to_custname,
    shipToAccount: row.ship_to_custnum,
    dateShipped: row.dateShipped,
  }))

  const columns = [
    {
      field: 'wms_order',
      headerName: Constants.orderAllocations.ORDER,
      flex: 1,
      minWidth: 100,
      renderCell: (cellValues) => {
        const order = cellValues.row.wms_order.toString()
        return (
          <LinkButton
            linkText={order}
            handleLinkClick={() => {
              handleOrderLinkClick(cellValues.row)
            }}
          />
        )
      },
    },
    {
      field: 'po',
      headerName: Constants.orderAllocations.PO_NUMBER,
      flex: 0.5,
      minWidth: 80,
      valueGetter: (value) => {
        return value || Constants.orderAllocations.NONE
      },
    },
    {
      field: 'lines',
      headerName: Constants.orderAllocations.LINES,
      flex: 0.5,
      minWidth: 80,
      align: 'right',
      headerAlign: 'right',
      valueGetter: (value) => {
        return value || Constants.orderAllocations.NONE
      },
    },
    {
      field: 'createdOn',
      minWidth: 200,
      headerName: Constants.orderAllocations.CREATED_ON,
      align: 'right',
      headerAlign: 'right',
      renderCell: (cellValues) => {
        return getDateTimeFormattedValue(cellValues.value)
      },
      flex: 1,
    },
    {
      field: 'orderStatus',
      minWidth: 100,
      headerName: Constants.orderAllocations.STATUS,
      renderCell: (cellValues) => {
        if (!cellValues.row.orderStatus) {
          return Constants.orderAllocations.PENDING
        }
        return <StatusChip status={cellValues.row.orderStatus} />
      },
      flex: 1,
    },
    {
      field: 'shipToName',
      headerName: Constants.orderAllocations.SHIP_TO_CUSTOMER,
      flex: 1,
      minWidth: 120,
      valueGetter: (value) => {
        return value || Constants.orderAllocations.NONE
      },
    },
    {
      field: 'shipToAccount',
      headerName: Constants.orderAllocations.SHIP_TO_ACCOUNT,
      flex: 1,
      minWidth: 150,
      valueGetter: (value) => {
        return value || Constants.orderAllocations.NONE
      },
    },
    {
      field: 'dateShipped',
      headerName: Constants.orderAllocations.DATE_SHIPPED,
      align: 'right',
      headerAlign: 'right',
      renderCell: (cellValues) => {
        return getDateTimeFormattedValue(cellValues.value) || Constants.orderAllocations.NONE
      },
      flex: 1,
      minWidth: 200,
    },
  ]

  const orderAllocationTableStyle = {
    minHeight: '160px',
    height: isFetchingOrderAllocations ? '400px' : 'auto',
  }

  return (
    <Grid sx={{ pt: 3 }}>
      <Paper>
        <DataGrid
          sx={orderAllocationTableStyle}
          rows={rows}
          columns={columns}
          loading={isFetchingOrderAllocations}
          pageSizeOptions={[25, 50]}
          spacing={1}
          sortingOrder={['asc', 'desc', null]}
          onSortModelChange={(newSortModel) => handleSortChange(newSortModel)}
          slotProps={{
            pagination: {
              count: Number(totalRowsCount),
              page: !totalRowsCount || totalRowsCount <= 0 ? 0 : Number(page),
              rowsPerPage,
              onPageChange: handleChangePage,
              onRowsPerPageChange: handleChangeRowsPerPage,
            },
            loadingOverlay: {
              variant: 'skeleton',
              noRowsVariant: 'skeleton',
            },
          }}
          slots={{
            pagination: GenericTablePagination,
          }}
          disableColumnFilter
          disableRowSelectionOnClick
        />
      </Paper>
      <GenericSnackbar />
    </Grid>
  )
}

export default WmsOrderAllocationsTable
